import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  playCorrectChime, 
  playWrongBuzzer, 
  playWinFanfare, 
  playCountdownBeep, 
  playTickSound 
} from '../utils/soundEffects';
import { 
  SCHOOL_HOUSE_TEAMS, 
  getHouseScores, 
  saveHouseScores,
  getEmojiRiddles,
  DEFAULT_EMOJI_RIDDLES,
  getCustomHouses,
  announceInterHouseChampion
} from '../utils/quizData';
import ConfettiEffect from '../components/ConfettiEffect';
import { useGameExitRoute } from '../utils/navigation';

const { 
  FiAward, FiClock, FiMaximize2, FiMinimize2, FiRefreshCw, 
  FiEye, FiArrowRight, FiSun, FiMoon, FiHome, FiHelpCircle, 
  FiCheck, FiX, FiUsers, FiImage 
} = FiIcons;

const LiveEmojiQuest = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [riddles, setRiddles] = useState(getEmojiRiddles());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(25);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false); // Default to bright Day Mode for classroom projectors
  const [houseScores, setHouseScores] = useState(getHouseScores());
  const [selectedOption, setSelectedOption] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeEmojiClue, setActiveEmojiClue] = useState(null);
  const [showRoundFinishedModal, setShowRoundFinishedModal] = useState(false);
  const [isRoundWinnerAnnounced, setIsRoundWinnerAnnounced] = useState(false);
  const [roundAwardType, setRoundAwardType] = useState('Emoji Quest Championship Trophy 🏆');
  const [roundPrizeDetails, setRoundPrizeDetails] = useState('First place trophy, medals & certificates presented on stage.');
  const [roundAnnouncedBy, setRoundAnnouncedBy] = useState('Quiz Master / Sports Coordinator');
  const [roundAnnouncedRecord, setRoundAnnouncedRecord] = useState(null);

  // Sync with custom riddles from Admin Dashboard
  useEffect(() => {
    const handleRiddlesUpdate = () => {
      const updated = getEmojiRiddles();
      setRiddles(updated);
    };
    window.addEventListener('emojiRiddlesUpdated', handleRiddlesUpdate);
    window.addEventListener('storage', handleRiddlesUpdate);
    return () => {
      window.removeEventListener('emojiRiddlesUpdated', handleRiddlesUpdate);
      window.removeEventListener('storage', handleRiddlesUpdate);
    };
  }, []);

  const currentRiddle = (riddles && riddles[currentIndex]) || (riddles && riddles[0]) || DEFAULT_EMOJI_RIDDLES[0];

  // Sync house scores with shared championship barometer
  useEffect(() => {
    const handleUpdate = () => setHouseScores(getHouseScores());
    window.addEventListener('houseScoresUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener('houseScoresUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  // Timer Countdown Hook
  useEffect(() => {
    let timer;
    if (isTimerActive && timeLeft > 0 && !isRevealed) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 6 && prev > 1) {
            playCountdownBeep(false);
          } else if (prev === 1) {
            playCountdownBeep(true);
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timeLeft === 0 && !isRevealed) {
      setIsTimerActive(false);
      playWrongBuzzer();
    }
    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft, isRevealed]);

  // Reset states when moving to next riddle
  useEffect(() => {
    setSelectedOption(null);
    setIsRevealed(false);
    setShowHint(false);
    setTimeLeft(25);
    setIsTimerActive(false);
    setActiveEmojiClue(null);
    setShowConfetti(false);
  }, [currentIndex, riddles]);

  const handleStartTimer = () => {
    setTimeLeft(25);
    setIsTimerActive(true);
    playTickSound(700, 0.08);
  };

  const handleOptionClick = (index) => {
    if (isRevealed) return; // Already revealed

    setSelectedOption(index);
    const isCorrect = index === currentRiddle.correctIndex;

    if (isCorrect) {
      setIsRevealed(true);
      setIsTimerActive(false);
      setShowConfetti(true);
      playCorrectChime();
      setTimeout(() => playWinFanfare(), 300);
    } else {
      playWrongBuzzer();
    }
  };

  const handleReveal = () => {
    setIsRevealed(true);
    setIsTimerActive(false);
    setSelectedOption(currentRiddle.correctIndex);
    setShowConfetti(true);
    playCorrectChime();
    setTimeout(() => playWinFanfare(), 350);
  };

  const handleNext = () => {
    const total = (riddles && riddles.length) || DEFAULT_EMOJI_RIDDLES.length;
    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      playTickSound(900, 0.05);
    } else {
      // Completed all riddles! Do not repeat from beginning - show round finished screen
      setShowRoundFinishedModal(true);
      playWinFanfare();
    }
  };

  const handleAnnounceRoundWinner = () => {
    if (isRoundWinnerAnnounced) return; // Prevent repeated / duplicate entries in log!

    const customHouses = getCustomHouses();
    const sortedHouses = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
    const winnerHouse = sortedHouses[0] || customHouses[0];
    const winningPoints = houseScores[winnerHouse.id] || 0;

    const record = announceInterHouseChampion({
      houseId: winnerHouse.id,
      houseName: winnerHouse.name,
      badge: winnerHouse.badge,
      points: winningPoints,
      awardType: roundAwardType,
      eventSource: 'Live Emoji Guessing Quest',
      prizeDistributionInfo: roundPrizeDetails,
      announcedBy: roundAnnouncedBy
    });

    setIsRoundWinnerAnnounced(true);
    setRoundAnnouncedRecord(record);
    playWinFanfare();
  };

  const handleRestartQuest = () => {
    setCurrentIndex(0);
    setShowRoundFinishedModal(false);
    setIsRoundWinnerAnnounced(false);
    setRoundAnnouncedRecord(null);
  };

  // Dual-credit Barometer scoring (+10 and -5 system matching LiveQuizArena & LiveWordScramble)
  const updateHouseScore = (houseId, points) => {
    const current = houseScores[houseId] || 0;
    const updated = { ...houseScores, [houseId]: Math.max(0, current + points) };
    setHouseScores(updated);
    saveHouseScores(updated);
    if (points > 0) {
      playCorrectChime();
    } else {
      playTickSound(400, 0.06);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };

  const handleEmojiTap = (idx) => {
    playTickSound(800 + idx * 120, 0.08);
    setActiveEmojiClue(idx);
    setTimeout(() => setActiveEmojiClue(null), 2400);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 flex flex-col justify-between p-4 md:p-8 select-none relative overflow-x-hidden ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-amber-50/90 via-sky-50/60 to-purple-50/80 text-slate-900'
    }`}>
      {showConfetti && <ConfettiEffect />}

      {/* TOP HEADER: Navigation, Day/Night & Projector Controls */}
      <header className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Link 
            to={exitRoute} 
            className={`p-2.5 rounded-2xl border transition-all ${
              isDarkMode ? 'bg-slate-900 border-slate-800 text-slate-300 hover:text-white' : 'bg-white border-slate-200 text-slate-700 shadow-xs hover:bg-slate-100'
            }`}
            title={exitTitle}
          >
            <SafeIcon icon={FiHome} className="text-lg" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl animate-bounce">🧩</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500">
                Live Emoji Riddle Quest
              </h1>
            </div>
            <p className="text-xs text-slate-500 font-medium">
              Live Stage & Projector Display • Riddle {currentIndex + 1} of {(riddles && riddles.length) || 1}
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {/* Day / Night Mode Toggle */}
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`px-3 py-2 rounded-xl text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
              isDarkMode ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md' : 'bg-white text-slate-800 border-slate-200 shadow-xs'
            }`}
            title="Toggle Day / Night Mode"
          >
            <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
            <span className="hidden sm:inline">{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
          </button>

          {/* Fullscreen Button */}
          <button 
            onClick={toggleFullscreen}
            className={`p-2.5 rounded-xl border transition-all cursor-pointer ${
              isDarkMode ? 'bg-slate-900 border-slate-800 text-slate-300' : 'bg-white border-slate-200 text-slate-700 shadow-xs'
            }`}
            title="Toggle Projector Fullscreen"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize2 : FiMaximize2} className="text-lg" />
          </button>
        </div>
      </header>

      {/* MAIN PROJECTOR RIDDLE STAGE */}
      <main className="my-6 flex-1 flex flex-col items-center justify-center max-w-4xl mx-auto w-full text-center">
        <motion.div 
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className={`w-full p-6 sm:p-10 rounded-3xl border shadow-xl relative overflow-hidden ${
            isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/95 border-purple-100 shadow-purple-200/40'
          }`}
        >
          {/* Category Pill & Live Timer Badge */}
          <div className="flex items-center justify-between mb-4">
            <span className="px-3.5 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-purple-500/15 text-purple-600 dark:text-purple-300 border border-purple-500/20">
              {currentRiddle.category || 'Visual Riddle Quest'}
            </span>

            <div className={`px-4 py-1.5 rounded-2xl font-mono font-black text-sm border flex items-center gap-1.5 transition-colors ${
              timeLeft <= 5 
                ? 'bg-red-500 text-white border-red-600 animate-pulse' 
                : isDarkMode 
                ? 'bg-slate-800 text-white border-slate-700' 
                : 'bg-slate-100 text-slate-800 border-slate-200'
            }`}>
              <SafeIcon icon={FiClock} className="text-lg" />
              <span>{timeLeft}s</span>
            </div>
          </div>

          {/* Optional Custom Image Clue for School / Event Owner */}
          {currentRiddle.image && (
            <div className="mb-4 flex justify-center">
              <div className="relative group max-w-sm rounded-2xl overflow-hidden border-2 border-purple-300/60 dark:border-purple-600/60 shadow-md">
                <img
                  src={currentRiddle.image}
                  alt={currentRiddle.answer}
                  className="w-full max-h-56 object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="absolute bottom-2 right-2 px-2.5 py-0.5 rounded-full bg-slate-900/80 text-white text-[10px] font-bold backdrop-blur-xs flex items-center gap-1">
                  <SafeIcon icon={FiImage} /> Visual Clue
                </span>
              </div>
            </div>
          )}

          <h2 className="text-base sm:text-lg font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider mb-2">
            Guess the Answer from the Clue Emojis!
          </h2>
          <p className="text-xs text-slate-400 mb-6">
            Tap an emoji to inspect clues, or select the correct answer below:
          </p>

          {/* Huge Animated Emojis (Clickable for sound and clues!) */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 my-2 py-3 bg-purple-500/5 dark:bg-purple-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-800/40">
            {(currentRiddle.emojis || ["❓", "❓", "❓", "❓"]).map((emoji, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: idx * 0.12 }}
                whileHover={{ scale: 1.3, rotate: 6 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleEmojiTap(idx)}
                className="relative text-5xl sm:text-7xl md:text-8xl drop-shadow-md cursor-pointer transition-transform select-none p-2 rounded-2xl hover:bg-white/60 dark:hover:bg-slate-800/60"
                title={`Clue ${idx + 1}: Click to inspect!`}
              >
                <span>{emoji}</span>
                {activeEmojiClue === idx && currentRiddle.emojiClues?.[idx] && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-purple-600 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow-lg z-30"
                  >
                    {currentRiddle.emojiClues[idx]}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {/* MULTIPLE CHOICE OPTIONS: Clickable Interactive Answer Grid */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-left">
            {(currentRiddle.options || []).map((opt, idx) => {
              const isSelected = selectedOption === idx;
              const isCorrectOpt = idx === currentRiddle.correctIndex;
              let optStyle = isDarkMode
                ? 'bg-slate-800/80 border-slate-700 text-slate-100 hover:border-purple-400 hover:bg-slate-800'
                : 'bg-slate-50 border-slate-200 text-slate-800 hover:border-purple-300 hover:bg-purple-50/50 shadow-xs';

              if (isRevealed && isCorrectOpt) {
                optStyle = 'bg-emerald-500 text-white border-emerald-600 shadow-lg shadow-emerald-500/30 font-black';
              } else if (isSelected && !isCorrectOpt) {
                optStyle = 'bg-rose-500 text-white border-rose-600 shadow-md animate-shake font-bold';
              }

              return (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => handleOptionClick(idx)}
                  className={`p-4 rounded-2xl border-2 transition-all flex items-center justify-between gap-3 text-sm cursor-pointer ${optStyle}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-xl bg-black/10 dark:bg-white/10 flex items-center justify-center font-black text-xs">
                      {['A', 'B', 'C', 'D'][idx]}
                    </span>
                    <span className="text-xl">{opt.emoji || '🎯'}</span>
                    <span className="font-bold text-sm sm:text-base leading-tight">
                      {opt.text}
                    </span>
                  </div>

                  {isRevealed && isCorrectOpt && (
                    <span className="w-7 h-7 rounded-full bg-white text-emerald-600 flex items-center justify-center flex-shrink-0 text-sm font-black shadow-sm">
                      <SafeIcon icon={FiCheck} />
                    </span>
                  )}
                  {isSelected && !isCorrectOpt && (
                    <span className="w-7 h-7 rounded-full bg-white text-rose-600 flex items-center justify-center flex-shrink-0 text-sm font-black shadow-sm">
                      <SafeIcon icon={FiX} />
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* REVEALED INSIGHT & DUAL-CREDIT HOUSE AWARD BAR (+10 and -5) */}
          <div className="mt-6 min-h-[90px] flex flex-col items-center justify-center">
            <AnimatePresence mode="wait">
              {isRevealed ? (
                <motion.div 
                  key="answer"
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.85, opacity: 0 }}
                  className="w-full bg-gradient-to-r from-emerald-500/15 via-teal-500/15 to-emerald-500/10 border-2 border-emerald-500/40 rounded-2xl p-5 shadow-lg text-left"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="text-xs font-black uppercase text-emerald-600 dark:text-emerald-400 tracking-widest flex items-center gap-1.5">
                      <SafeIcon icon={FiCheck} /> Correct Answer: {currentRiddle.answer}
                    </div>
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                      Score Inter-House Championship Points:
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium mb-3">
                    💡 <span className="font-bold">Did You Know?</span> {currentRiddle.funFact}
                  </p>

                  {/* Dual-Credit House Points (+10 and -5) */}
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-emerald-500/20">
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-500">
                      Award House:
                    </span>
                    {SCHOOL_HOUSE_TEAMS.map(team => (
                      <div key={team.id} className="flex items-center gap-1 bg-white/70 dark:bg-slate-800/80 p-1 rounded-xl border border-emerald-500/30 shadow-xs">
                        <span className="text-xs font-bold px-1.5 flex items-center gap-1">
                          <span>{team.badge}</span>
                          <span className="hidden sm:inline">{team.name}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => updateHouseScore(team.id, 10)}
                          className={`px-2 py-0.5 rounded-lg text-xs font-black text-white bg-gradient-to-r ${team.color} shadow-xs hover:scale-105 active:scale-95 transition-transform cursor-pointer`}
                          title="Award +10"
                        >
                          +10
                        </button>
                        <button
                          type="button"
                          onClick={() => updateHouseScore(team.id, -5)}
                          className="px-1.5 py-0.5 rounded-lg text-xs font-black text-red-600 bg-red-500/10 hover:bg-red-500 hover:text-white border border-red-500/20 shadow-xs hover:scale-105 active:scale-95 transition-transform cursor-pointer"
                          title="Deduct -5"
                        >
                          -5
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : showHint ? (
                <motion.div 
                  key="hint"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`w-full p-4 rounded-xl border text-xs sm:text-sm font-semibold text-left ${
                    isDarkMode ? 'bg-amber-950/40 border-amber-500/40 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}
                >
                  🔍 <span className="font-bold">Clue:</span> {currentRiddle.hint}
                </motion.div>
              ) : (
                <div className="text-slate-400 text-xs sm:text-sm font-medium flex items-center gap-2">
                  <span>Audience or House Team: Tap an option above or start countdown!</span>
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* CONTROL ACTION BUTTONS */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-800">
            {!isTimerActive && !isRevealed && (
              <button
                type="button"
                onClick={handleStartTimer}
                className="px-5 py-2.5 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/30 transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                <SafeIcon icon={FiClock} />
                <span>Start 25s Countdown</span>
              </button>
            )}

            {!isRevealed && (
              <button
                type="button"
                onClick={() => setShowHint(!showHint)}
                className={`px-4 py-2.5 rounded-2xl font-bold text-xs sm:text-sm flex items-center gap-2 border transition-all cursor-pointer ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700 text-amber-400 hover:bg-slate-700' 
                    : 'bg-white border-slate-300 text-amber-600 hover:bg-amber-50 shadow-xs'
                }`}
              >
                <SafeIcon icon={FiHelpCircle} />
                <span>{showHint ? 'Hide Clue' : 'Show Clue 🔍'}</span>
              </button>
            )}

            {!isRevealed ? (
              <button
                type="button"
                onClick={handleReveal}
                className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/30 transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                <SafeIcon icon={FiEye} />
                <span>Reveal Answer ✨</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                className="px-7 py-3 rounded-2xl bg-gradient-to-r from-indigo-600 to-pink-600 hover:from-indigo-500 hover:to-pink-500 text-white font-black text-sm flex items-center gap-2 shadow-lg shadow-pink-500/30 transition-all hover:scale-105 active:scale-95 cursor-pointer animate-bounce"
              >
                <span>
                  {currentIndex === ((riddles && riddles.length) || DEFAULT_EMOJI_RIDDLES.length) - 1
                    ? 'Finish & Announce Winner 🏆'
                    : 'Next Emoji Riddle'}
                </span>
                <SafeIcon icon={currentIndex === ((riddles && riddles.length) || DEFAULT_EMOJI_RIDDLES.length) - 1 ? FiAward : FiArrowRight} />
              </button>
            )}
          </div>
        </motion.div>
      </main>

      {/* FOOTER: School House Championship Barometer with +10 and -5 System (Matches LiveWordScramble & LiveQuizArena) */}
      <footer className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="text-indigo-500 text-sm" />
            <span>School House Championship Barometer (Live Scores)</span>
          </div>
          <button 
            type="button"
            onClick={() => {
              if (window.confirm("Reset all house scores to zero?")) {
                const reset = { red: 0, blue: 0, green: 0, yellow: 0 };
                setHouseScores(reset);
                saveHouseScores(reset);
              }
            }}
            className="text-[11px] font-bold text-slate-500 hover:text-red-500 flex items-center gap-1 cursor-pointer"
          >
            <SafeIcon icon={FiRefreshCw} /> Reset Barometer
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SCHOOL_HOUSE_TEAMS.map(team => {
            const score = houseScores[team.id] || 0;
            return (
              <div 
                key={team.id}
                className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 shadow-xs transition-colors ${
                  isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{team.badge}</span>
                  <div>
                    <span className={`text-xs font-bold block leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {team.name}
                    </span>
                    <span className="text-[10px] font-mono text-amber-500 font-black">{score} pts</span>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => updateHouseScore(team.id, 10)}
                    className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-600 dark:text-emerald-300 hover:text-white font-black text-[10px] rounded-lg border border-emerald-500/30 transition-colors cursor-pointer"
                    title="Award +10"
                  >
                    +10
                  </button>
                  <button
                    type="button"
                    onClick={() => updateHouseScore(team.id, -5)}
                    className="px-1.5 py-1 bg-red-500/10 hover:bg-red-500 text-red-600 dark:text-red-400 hover:text-white font-black text-[10px] rounded-lg border border-red-500/30 transition-colors cursor-pointer"
                    title="Deduct -5"
                  >
                    -5
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </footer>

      {/* Round Finished & Inter-House Winner Announcement Modal */}
      <AnimatePresence>
        {showRoundFinishedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className={`w-full max-w-xl rounded-3xl p-6 sm:p-7 shadow-2xl border ${
                isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200 text-slate-900'
              }`}
            >
              {/* Header */}
              <div className="text-center mb-5">
                <div className="w-16 h-16 rounded-2xl bg-amber-500/20 text-amber-500 mx-auto flex items-center justify-center text-3xl mb-3 shadow-inner">
                  🏆
                </div>
                <h3 className="text-xl sm:text-2xl font-black">
                  Emoji Quest Round Completed!
                </h3>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  All emoji riddles answered! Announce the official Inter-House Champion below.
                </p>
              </div>

              {/* House Points Leaderboard */}
              <div className="mb-5">
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
                  Final House Points Standings:
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(() => {
                    const customHouses = getCustomHouses();
                    const sorted = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
                    return sorted.map((house, idx) => (
                      <div
                        key={house.id}
                        className={`p-2.5 rounded-xl border text-center relative ${
                          idx === 0
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 ring-1 ring-amber-400'
                            : isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        {idx === 0 && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.2 rounded-full bg-amber-400 text-slate-950 text-[9px] font-black uppercase">
                            👑 Winner
                          </span>
                        )}
                        <div className="text-2xl mt-1">{house.badge || '🏆'}</div>
                        <div className="text-xs font-black truncate">{house.name}</div>
                        <div className="text-xs font-mono font-bold text-slate-400 mt-0.5">
                          {houseScores[house.id] || 0} pts
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Winner Announcement Section (Prevents repeated / duplicate logs) */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 mb-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-500 flex items-center gap-1.5">
                    <SafeIcon icon={FiAward} /> Winner Announcement to Sports Day Log
                  </span>
                  {isRoundWinnerAnnounced && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase">
                      ✓ Logged Once
                    </span>
                  )}
                </div>

                {!isRoundWinnerAnnounced ? (
                  <>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Award Trophy / Plaque Title:
                      </label>
                      <input
                        type="text"
                        value={roundAwardType}
                        onChange={e => setRoundAwardType(e.target.value)}
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Prize Distribution Information:
                      </label>
                      <textarea
                        rows={2}
                        value={roundPrizeDetails}
                        onChange={e => setRoundPrizeDetails(e.target.value)}
                        placeholder="Details for distributing prizes on stage..."
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs font-medium outline-none"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleAnnounceRoundWinner}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs shadow-md hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <SafeIcon icon={FiAward} />
                      <span>Announce Winner to Sports Day Log & Champions 🏆</span>
                    </button>
                  </>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs space-y-1">
                    <div className="font-black flex items-center gap-1.5 text-emerald-400">
                      <span>✓ Official Winner Announcement Logged</span>
                    </div>
                    <div>
                      <strong>{roundAnnouncedRecord?.houseName}</strong> crowned as <strong>{roundAnnouncedRecord?.awardType}</strong> ({roundAnnouncedRecord?.points} pts).
                    </div>
                    <div className="text-[11px] text-slate-400">
                      Recorded in Sports Day Log & Match History for prize coordination. Announcement will not repeat.
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700/60">
                <button
                  type="button"
                  onClick={() => setShowRoundFinishedModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-slate-200 text-xs font-bold"
                >
                  Close Summary
                </button>

                <button
                  type="button"
                  onClick={handleRestartQuest}
                  className="px-5 py-2.5 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-md cursor-pointer"
                >
                  <SafeIcon icon={FiRefreshCw} />
                  <span>Start New Emoji Quest</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveEmojiQuest;
